import { graphHooks } from '@tunasong/graph-lib/react'
import { Note as NoteIcon } from '@tunasong/icons'
import type { Note } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { usePerformanceConfig } from './part/performance-config.hook.js'
import styles from './notes-toggle.module.css'

interface NotesToggleProps {
  entity: Persisted<Entity>
  /** Phones: an icon with the count as a badge, named as on desktop */
  compact?: boolean
}

/**
 * Perform header "My notes (n)": turns the performer's private notes on or off. The choice is remembered
 * per user (profile config) and the notes never open on their own.
 *
 * Shares the RTK Query cache key with `EntityNotes`, so showing the notes does not refetch.
 */
export const NotesToggle: FC<NotesToggleProps> = ({ entity, compact = false }) => {
  const { entities: notes } = graphHooks.useEntityChildren<Note>({ parentId: entity.id, filter: 'note' })
  const { config, updatePerformanceConfig } = usePerformanceConfig()
  const visible = config.notesVisible ?? false
  const count = notes.length
  const label = count > 0 ? `My notes (${count})` : 'My notes'
  const handleClick = () => updatePerformanceConfig({ notesVisible: !visible })

  if (compact) {
    return (
      <IconButton
        className={[styles.compact, visible ? styles.on : undefined].filter(Boolean).join(' ')}
        aria-label={label}
        aria-pressed={visible}
        onClick={handleClick}
      >
        <NoteIcon />
        {count > 0 ? (
          <span aria-hidden="true" className={styles.badge}>
            {count}
          </span>
        ) : null}
      </IconButton>
    )
  }

  return (
    <Button
      variant={visible ? 'contained' : 'text'}
      color="inherit"
      size="small"
      aria-pressed={visible}
      onClick={handleClick}
    >
      {label}
    </Button>
  )
}

export default NotesToggle
